import { DataInputType, OutputContractKind, StepOutputContract } from '@/types/automationFields.ts';
import { IntegrationInputContract } from '@/types/integrations.ts';

export type InputContractStatus = {
  label: string;
  detail: string;
  color: 'yellow' | 'red';
};

type SelectedInput = {
  sourceType: DataInputType;
  outputContract: StepOutputContract | null;
  sourceAvailable?: boolean;
};

export const getInputContractDescription = (
  description: string | null | undefined,
  contract: IntegrationInputContract | null | undefined,
): string | undefined => {
  if (!contract) return description ?? undefined;
  const requirements = [
    contract.accepted_dataset_types.length > 0
      ? `Accepts ${contract.accepted_dataset_types.join(' or ')} data.`
      : null,
    contract.accepted_schema_refs.length > 0
      ? `Accepts schema ${contract.accepted_schema_refs.map((ref) => `${ref.schema_id}.v${ref.version}`).join(' or ')}.`
      : null,
  ];
  return [description, contract.purpose, ...requirements].filter(Boolean).join(' ') || undefined;
};

const mismatchStatus = (
  contract: IntegrationInputContract,
  detail: string,
): InputContractStatus => {
  const isStrict = contract.validation_mode === 'error';
  return {
    label: isStrict ? 'Choose a compatible input' : 'Input compatibility needs review',
    detail: `${detail} ${
      isStrict ? 'Choose a different input to continue.' : 'It remains allowed under the current policy.'
    }`,
    color: isStrict ? 'red' : 'yellow',
  };
};

export const getInputContractStatus = (
  contract: IntegrationInputContract | null | undefined,
  selectedInputs: SelectedInput[],
): InputContractStatus | null => {
  if (!contract) return null;
  if (selectedInputs.length === 0) {
    return contract.required
      ? mismatchStatus(contract, 'Choose an input.')
      : null;
  }

  if (!contract.allow_multiple && selectedInputs.length > 1) {
    return mismatchStatus(contract, 'Choose one input.');
  }

  const unsupportedSource = selectedInputs.some(
    ({ sourceType }) =>
      contract.allowed_source_types.length > 0 && !contract.allowed_source_types.includes(sourceType),
  );
  if (unsupportedSource) {
    return mismatchStatus(
      contract,
      `This input only accepts source type(s): ${contract.allowed_source_types.join(', ')}.`,
    );
  }

  const unavailableSource = selectedInputs.some(({ sourceAvailable }) => sourceAvailable === false);
  if (unavailableSource) {
    return {
      label: 'Input details unavailable',
      detail: 'Velos could not load this input’s details. Try again before relying on compatibility.',
      color: 'yellow',
    };
  }

  const dynamicInputs = selectedInputs.filter(({ sourceType }) => sourceType === DataInputType.Dynamic);
  if (dynamicInputs.length === 0) {
    return {
      label: 'Input structure can’t be verified',
      detail: 'Velos can’t verify this saved input’s structure.',
      color: 'yellow',
    };
  }

  const missingSourceContract = dynamicInputs.some(
    ({ outputContract }) =>
      outputContract === null ||
      outputContract.contract.kind === OutputContractKind.None ||
      outputContract.contract.kind === OutputContractKind.Incompatible,
  );
  if (missingSourceContract) {
    return mismatchStatus(contract, 'Velos can’t verify the selected input’s structure.');
  }

  const outputContracts = dynamicInputs.flatMap(({ outputContract }) => (outputContract ? [outputContract] : []));
  const typeMismatch = outputContracts.some(
    ({ contract: output }) =>
      contract.accepted_dataset_types.length > 0 &&
      !contract.accepted_dataset_types.includes(output.dataset_type),
  );
  if (typeMismatch) {
    return mismatchStatus(contract, `This input must provide ${contract.accepted_dataset_types.join(' or ')} data.`);
  }

  const schemaMismatch = outputContracts.some(
    ({ contract: output }) =>
      contract.accepted_schema_refs.length > 0 &&
      !contract.accepted_schema_refs.some(
        (ref) => ref.schema_id === output.schema_id && ref.version === output.version,
      ),
  );
  if (schemaMismatch) {
    return mismatchStatus(
      contract,
      `This input must provide schema ${contract.accepted_schema_refs.map((ref) => `${ref.schema_id}.v${ref.version}`).join(' or ')}.`,
    );
  }

  // An input without contract metadata needs review, but must not hide an
  // incompatible dynamic input in the same multi-input binding.
  const legacyInput = dynamicInputs.length !== selectedInputs.length;
  if (legacyInput) {
    return {
      label: 'Input structure can’t be verified',
      detail: 'Velos can’t verify one or more saved inputs’ structures.',
      color: 'yellow',
    };
  }

  // A compatible selection does not require attention, so keep the editor
  // quiet rather than rendering a routine success badge.
  return null;
};
